import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Quote, ArrowRight } from "lucide-react";
export default function TheStoryBehindTheSpace() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-story-behind-the-space" className="bg-background py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-muted/30 to-transparent pointer-events-none" />

      <div ref={ref} className={`transition-opacity duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="max-w-[700px] mx-auto px-6 md:px-8">
          {}
          <header className="mb-12 text-center md:text-left">
            <span className="text-primary font-medium tracking-widest text-xs uppercase mb-4 block">
              Our Philosophy
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.1] mb-6">
              The Story Behind the Space
            </h2>
            <div className="w-16 h-1 bg-primary/20 mx-auto md:mx-0 mb-8" />
          </header>

          {}
          <article className="prose prose-stone prose-lg max-w-none text-muted-foreground font-sans leading-relaxed">
            <p className="mb-8 text-lg md:text-xl text-foreground/90">
              <span className="float-left text-7xl md:text-8xl font-serif text-primary leading-[0.8] pr-4 pt-2 mt-[-0.2em]">
                I
              </span>
              t began with a simple, quiet realization: our outer environment is
              often a direct reflection of our inner state. In a world that
              constantly demands more—more things, more speed, more noise—we
              found ourselves craving less. We wanted a sanctuary, a place where
              the mind could rest and the eyes could travel freely without
              stumbling over clutter.
            </p>

            <p className="mb-10">Living Organized Bliss was born in Springfield not just as a blog about tidying up, but as a movement toward intentional living. We believe that organization isn't about rigid perfection or sterile surfaces. It is about creating room for life to happen. It is about clearing the path so you can focus on what truly matters to you and your family.</p>

            {}
            <figure className="my-12 -mx-6 md:-mx-12">
              <div className="relative aspect-[16/10] overflow-hidden rounded-sm bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573125/img/serene_morning_sunlight_living_room.png" alt="Sunlight streaming into a calm, minimalist living room" className="object-cover w-full h-full hover:scale-105 transition-transform duration-700 ease-in-out" />
              </div>
              <figcaption className="mt-3 text-center text-sm text-muted-foreground italic font-serif">
                Morning light in a space designed for clarity.
              </figcaption>
            </figure>

            <h3 className="font-serif text-2xl md:text-3xl text-foreground mt-12 mb-6">
              The Art of "Quiet Luxury"
            </h3>

            <p className="mb-8">
              We draw our inspiration from the concept of "Warm Minimalism." It
              is an aesthetic that marries the clean lines of modern design with
              the tactile warmth of natural materials. Think soft beige linens,
              warm white ceramics, and the grounding presence of sage green
              accents. It is luxury, but not in the sense of opulence. It is the
              luxury of time, of space, and of peace.
            </p>

            <p className="mb-10">
              Every guide we write, from decluttering checklists to storage
              solutions, is crafted with this philosophy in mind. We don't just
              want you to hide your mess; we want to help you curate a home that
              feels like a deep exhale at the end of a long day.
            </p>

            {}
            <blockquote className="border-l-2 border-primary/30 pl-6 py-2 my-12 italic text-foreground font-serif text-xl md:text-2xl leading-relaxed bg-muted/30 p-6 rounded-r-lg">
              <Quote className="w-8 h-8 text-primary/40 mb-4" />
              "A calm home is the foundation for a calm life. When we clear our
              physical spaces, we often find we've cleared our minds as well."
            </blockquote>

            <h3 className="font-serif text-2xl md:text-3xl text-foreground mt-12 mb-6">
              Designed for Real Life
            </h3>

            <p className="mb-8">
              While we love the aesthetic of a perfectly organized pantry, we
              know that life is messy. Kids leave toys out, mail piles up, and
              laundry is a never-ending cycle. That is why our approach is
              rooted in practicality.
            </p>

            {}
            <figure className="my-12">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[3/4] overflow-hidden rounded-sm bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573125/img/organized_kitchen_drawer_detail.png" alt="Detail of an organized kitchen drawer with bamboo dividers" className="object-cover w-full h-full hover:scale-105 transition-transform duration-700 ease-in-out" />
                </div>
                <div className="aspect-[3/4] overflow-hidden rounded-sm bg-muted mt-8">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573125/img/minimalist_shelf_decor_vases.png" alt="Minimalist shelf styling with ceramic vases" className="object-cover w-full h-full hover:scale-105 transition-transform duration-700 ease-in-out" />
                </div>
              </div>
              <figcaption className="mt-3 text-center text-sm text-muted-foreground italic font-serif">
                Functionality meets beauty in everyday details.
              </figcaption>
            </figure>

            <p className="mb-12">
              Emily Johnson, our founder, started The Organized Space to bridge
              the gap between aspirational magazine spreads and the reality of a
              busy family home. Our systems are designed to be maintained, not
              just admired. Whether you are a renter looking for temporary
              solutions or a homeowner ready for a full renovation, we are here
              to guide you through the process, one drawer at a time.
            </p>

            {}
            <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row items-center md:items-start gap-6">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-muted shrink-0 border border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1573125/img/emily_johnson_founder_portrait.png" alt="Emily Johnson" className="w-full h-full object-cover" />
              </div>
              <div className="text-center md:text-left">
                <p className="font-serif text-xl text-foreground mb-1">
                  Emily Johnson
                </p>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-4">
                  Founder & Home Organization Expert
                </p>
                <div className="flex justify-center md:justify-start">
                  <Button asChild variant="outline" className="group">
                    <Link to="/ContactPage">
                      Get in Touch <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>;
}